import type { Logger, Unsubscribe } from '../core/services';

/**
 * While something is live (streaming or recording): ask before the tab is
 * closed and hold a screen wake lock so the machine does not sleep mid-show.
 * The wake lock is released by the browser whenever the tab is hidden, so it is
 * re-requested when the tab becomes visible again. A Web Lock is also held so
 * the browser does not freeze or discard the page while it is live in the background.
 * `beforeUnload: false` (embedded in the portal) leaves the close prompt to the embedding page.
 */
export function createLiveGuard(
  isLive: () => boolean,
  logger: Logger,
  options: { beforeUnload?: boolean } = {},
): { refresh(): void; dispose: Unsubscribe } {
  const guardUnload = options.beforeUnload ?? true;
  let lock: WakeLockSentinel | null = null;
  let requesting = false;

  const acquire = async () => {
    if (lock || requesting || !('wakeLock' in navigator) || document.visibilityState !== 'visible') return;
    requesting = true;
    try {
      const sentinel = await navigator.wakeLock.request('screen');
      sentinel.addEventListener('release', () => {
        if (lock === sentinel) lock = null;
      });
      if (isLive()) lock = sentinel;
      else void sentinel.release();
    } catch (error) {
      logger.log('debug', 'studio', 'Screen wake lock was not granted', error);
    } finally {
      requesting = false;
    }
  };

  // A held Web Lock marks the page as doing work the browser should not freeze
  // or discard while it is in the background. It is released by resolving the
  // promise the lock callback returned.
  let releaseWebLock: (() => void) | null = null;
  const holdWebLock = () => {
    if (releaseWebLock || !navigator.locks) return;
    const held = new Promise<void>((resolve) => {
      releaseWebLock = resolve;
    });
    navigator.locks.request('ohmyobs-live', { mode: 'shared' }, () => held).catch((error: unknown) => {
      releaseWebLock = null;
      logger.log('debug', 'studio', 'Web Lock was not granted', error);
    });
  };
  const dropWebLock = () => {
    releaseWebLock?.();
    releaseWebLock = null;
  };

  const refresh = () => {
    if (isLive()) {
      void acquire();
      holdWebLock();
    } else {
      dropWebLock();
      if (lock) {
        void lock.release();
        lock = null;
      }
    }
  };

  const onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (isLive()) event.preventDefault();
  };
  if (guardUnload) window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('visibilitychange', refresh);

  return {
    refresh,
    dispose() {
      if (guardUnload) window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', refresh);
      dropWebLock();
      if (lock) void lock.release();
      lock = null;
    },
  };
}
